import { useState, useEffect, useCallback } from 'react'
import type { UserProgressDto } from '@/services/stories.service'
import { getUserProgress } from '@/services/stories.service'
import { useMostRecentInProgressStory } from '@/hooks/useMostRecentInProgressStory'

interface UseTopNavigationProgressServices {
    /** Function to get user progress (defaults to getUserProgress) */
    getUserProgress?: () => Promise<UserProgressDto[]>
}

interface UseTopNavigationProgressReturn {
    userProgress: UserProgressDto[]
    progressLoading: boolean
    mostRecentStory: ReturnType<typeof useMostRecentInProgressStory>
    refreshUserProgress: () => Promise<void>
}

export const useTopNavigationProgress = (
    user: { id: string } | null,
    services?: UseTopNavigationProgressServices
): UseTopNavigationProgressReturn => {
    const getUserProgressFn = services?.getUserProgress ?? getUserProgress
    const [userProgress, setUserProgress] = useState<UserProgressDto[]>([])
    const [progressLoading, setProgressLoading] = useState(false)

    const safeUserProgress = Array.isArray(userProgress) ? userProgress : []
    const mostRecentStory = useMostRecentInProgressStory(safeUserProgress, progressLoading)

    // Stabilize refreshUserProgress - getUserProgressFn should be stable
    const refreshUserProgress = useCallback(async () => {
        if (!user) return

        try {
            const progressData = await getUserProgressFn()
            setUserProgress(progressData)
        } catch (error) {
            console.error('Failed to refresh user progress:', error)
            setUserProgress([])
        }
    }, [user, getUserProgressFn])

    // Expose refresh function globally so other components can call it
    useEffect(() => {
        if (typeof window !== 'undefined') {
            ;(
                window as Window & { refreshTopNavigationProgress?: () => Promise<void> }
            ).refreshTopNavigationProgress = refreshUserProgress
        }
    }, [refreshUserProgress])

    // Fetch user progress when user is available
    useEffect(() => {
        if (!user) {
            setUserProgress([])
            setProgressLoading(false)
            return
        }

        let mounted = true

        const fetchProgress = async () => {
            setProgressLoading(true)
            try {
                const progressData = await getUserProgressFn()
                if (mounted) {
                    setUserProgress(progressData)
                }
            } catch (error) {
                console.error('Failed to fetch user progress:', error)
                if (mounted) {
                    setUserProgress([])
                }
            } finally {
                if (mounted) {
                    setProgressLoading(false)
                }
            }
        }

        void fetchProgress()

        return () => {
            mounted = false
        }
    }, [user, getUserProgressFn])

    return { userProgress, progressLoading, mostRecentStory, refreshUserProgress }
}
